// ─── Shared visual language for the vendor map ────────────────────────────────
// Used by BOTH the admin SlotGrid and the vendor Step4ChooseSpot so the two
// views can never visually drift apart. If you add a new tent type, add it
// here once and both sides pick it up.

// Sub-slot layout definitions per tent type
export const TENT_SUBDIVISIONS = {
  "17x17-pod":    { subs: ["A", "B", "C", "D"], layout: "g22", label: "4-vendor pod (2×2)" },
  "20x20-double": { subs: ["A", "B"],           layout: "g12", label: "Shared 20×20 (1×2)" },
  "20x20-single": { subs: ["A"],                layout: "g11", label: "Private 20×20" },
  "20x20":        { subs: ["A"],                layout: "g11", label: "20×20" },
  "10x10-single": { subs: ["A"],                layout: "g11", label: "10×10 Single" },
  "Food Court":   { subs: ["A"],                layout: "g11", label: "Food Court" },
  "sponsored":    { subs: ["A"],                layout: "g11", label: "Sponsored" },
  "space":        { subs: ["A"],                layout: "g11", label: "Space" },
  "reserved":     { subs: [],                   layout: "g11", label: "Reserved" },
};

export const TENT_COLORS = {
  "17x17-pod":    "#ffaa6e",
  "20x20-double": "#74bfed",
  "20x20-single": "#bc8fe8",
  "20x20":        "#d9d9d9",
  "10x10-single": "#ff8080",
  "Food Court":   "#5ee8a8",
  "20x20_open":   "#64cff5",
  "sponsored":    "#ffe44d",
  reserved:       "#f590b0",
  space:          "#feca62",
};

export const TENT_LABELS = {
  "17x17-pod":    "17×17 Pod",
  "20x20-double": "20×20 Sharing",
  "20x20-single": "20×20 Individual",
  "10x10-single": "10×10 Single",
  "20x20":        "20×20",
  reserved:       "Reserved",
  "Food Court":   "Food Court",
  space:          "Space",
};

export const FACING_LABEL = {
  right: "Opens → right",
  left:  "Opens ← left",
  top:   "Opens ↑ top",
  down:  "Opens ↓ bottom",
};

export const SUBTLE = "2px solid rgba(0,0,0,0.12)";
export const OPEN   = "3px solid #22c55e";

export const getFacingBorders = (facing) => ({
  borderTop:    facing === "top"   ? OPEN : SUBTLE,
  borderBottom: facing === "down"  ? OPEN : SUBTLE,
  borderLeft:   facing === "left"  ? OPEN : SUBTLE,
  borderRight:  facing === "right" ? OPEN : SUBTLE,
});

export const getArrowStyle = (facing) => {
  const base = { position: "absolute", zIndex: 2, pointerEvents: "none" };
  switch (facing) {
    case "right": return { ...base, right: 3,  top: "50%",  transform: "translateY(-50%) rotate(0deg)" };
    case "left":  return { ...base, left: 3,   top: "50%",  transform: "translateY(-50%) rotate(180deg)" };
    case "top":   return { ...base, top: 3,    left: "50%", transform: "translateX(-50%) rotate(-90deg)" };
    case "down":  return { ...base, bottom: 3, left: "50%", transform: "translateX(-50%) rotate(90deg)" };
    default:      return base;
  }
};

export const FacingArrow = ({ facing }) => (
  <svg style={getArrowStyle(facing)} width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden>
    <path d="M2 5h6M5 2l3 3-3 3" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const CrownIcon = () => (
  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-label="Self-selected">
    <path d="M1 9h10M1.5 9L2.5 4l3 3 1-4 1 4 3-3 1 5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

export const PencilIcon = () => (
  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-label="Admin assigned">
    <path d="M8 2l2 2-6 6H2V8l6-6z" stroke="#6366f1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

// ─── Orientation-aware partitions ──────────────────────────────────────────
// A tile's `layout` (g11 / g12 / g22) describes the SHAPE of the split, but
// not which way it should run — that depends on which edge is open
// (`facing`). A "g12" tile (two subs side by side) is drawn for a tile that
// opens top/down: both subs sit shoulder-to-shoulder facing the same
// street. If that tile instead opens left/right, the two subs need to be
// stacked top/bottom instead — each one keeps its own full-width frontage
// on the open (left/right) edge, rather than one sub blocking the other's
// storefront.
//
// "g22" pods are symmetric on all four sides, so they never need
// reorientation.
//
// This returns inline grid-template overrides — safe to spread onto the
// same className-based subgrid, no CSS file changes required.
export const getOrientedLayoutStyle = (layout, facing) => {
  if (layout === "g12") {
    if (facing === "left" || facing === "right") {
      return { gridTemplateColumns: "1fr", gridTemplateRows: "1fr 1fr" };
    }
    // default: top/down-facing (or no facing set) → side by side
    return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr" };
  }
  if (layout === "g22") {
    return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" };
  }
  return undefined; // g11 — single slot, nothing to orient
};

// Small preview grid shown inside a map tile (2×2 pod / 1×2 shared / 1×1),
// pure display — no click handlers, safe to reuse on both admin and vendor grids.
// `facing` is optional — pass the slot's facing so shared (g12) tiles split
// in the correct direction relative to their open edge.
export const TileSubPreview = ({ slotId, subBookings, effectiveType, facing }) => {
  const def = TENT_SUBDIVISIONS[effectiveType];
  if (!def || def.subs.length <= 1) return null;

  const orientedStyle = getOrientedLayoutStyle(def.layout, facing);

  return (
    <div
      className={`tv-tile-subgrid tv-tile-subgrid--${def.layout}`}
      style={orientedStyle}
    >
      {def.subs.map(sub => {
        const key   = `${slotId}:${sub}`;
        const entry = subBookings[key];
        return (
          <div
            key={sub}
            className={`tv-tile-subcell ${entry ? "tv-tile-subcell--filled" : "tv-tile-subcell--empty"}`}
            title={entry ? entry.vendorName : `${slotId}${sub} — empty`}
          >
            {entry
              ? <span className="tv-tile-subcell-init">{entry.vendorName?.[0]?.toUpperCase()}</span>
              : <span className="tv-tile-subcell-id">{sub}</span>}
          </div>
        );
      })}
    </div>
  );
};